import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../core/apiClient';

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

interface Absence {
  id: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  notes: string;
}

interface Summary {
  vacation: number;
  sick_leave: number;
  personal: number;
  maternity: number;
  paternity: number;
  other: number;
}

interface EmployeeCalendarProps {
  employeeId: string;
  employeeName: string;
}

const ABSENCE_COLORS: Record<string, { bg: string; label: string }> = {
  vacation: { bg: 'bg-blue-500', label: 'Vacances' },
  sick_leave: { bg: 'bg-red-500', label: 'Baixa IT' },
  personal: { bg: 'bg-green-500', label: 'Permis' },
  maternity: { bg: 'bg-purple-500', label: 'Maternitat' },
  paternity: { bg: 'bg-purple-400', label: 'Paternitat' },
  other: { bg: 'bg-gray-500', label: 'Altres' },
};

const MONTH_NAMES = [
  'Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'
];

export default function EmployeeCalendar({ employeeId, employeeName: _employeeName }: EmployeeCalendarProps) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  // Suppress unused variable warning - may be used in future for header
  void _employeeName;

  useEffect(() => {
    loadCalendar();
  }, [employeeId, year]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: { holidays: Holiday[]; absences: Absence[]; summary: Summary } }>(`/calendar/employee/${employeeId}?year=${year}`);
      if (response.success && response.data) {
        setHolidays(response.data.holidays || []);
        setAbsences(response.data.absences || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      console.error('Error loading employee calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mapa de festius per data
  const holidayMap = useMemo(() => {
    const map = new Map<string, Holiday>();
    holidays.forEach(h => {
      const dateKey = h.date.split('T')[0];
      map.set(dateKey, h);
    });
    return map;
  }, [holidays]);

  // Mapa d'absencies per data
  const absenceMap = useMemo(() => {
    const map = new Map<string, Absence>();
    absences.forEach(a => {
      const start = new Date(a.startDate);
      const end = new Date(a.endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().split('T')[0];
        map.set(key, a);
      }
    });
    return map;
  }, [absences]);

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getDayClass = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    // Comprovar absència
    const absence = absenceMap.get(dateStr);
    if (absence) {
      return ABSENCE_COLORS[absence.type]?.bg || 'bg-gray-400';
    }
    
    // Comprovar festiu
    if (holidayMap.has(dateStr)) {
      return 'bg-amber-400';
    }
    
    // Cap de setmana
    if (isWeekend(date)) {
      return 'bg-gray-200';
    }
    
    return 'bg-white border border-gray-200';
  };

  const getDayTitle = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    const absence = absenceMap.get(dateStr);
    if (absence) {
      return `${ABSENCE_COLORS[absence.type]?.label || absence.type}${absence.notes ? `: ${absence.notes}` : ''}`;
    }
    
    const holiday = holidayMap.get(dateStr);
    if (holiday) {
      return holiday.name;
    }
    
    return '';
  };

  // Generar calendari per mes
  const renderMonth = (monthIndex: number) => {
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startDay = firstDay.getDay(); // 0 = diumenge
    
    const days: (Date | null)[] = [];
    
    // Dies buits al principi
    for (let i = 0; i < (startDay === 0 ? 6 : startDay - 1); i++) {
      days.push(null);
    }
    
    // Dies del mes
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, monthIndex, d));
    }
    
    return (
      <div key={monthIndex} className="space-y-1">
        <h4 className="text-sm font-medium text-center">{MONTH_NAMES[monthIndex]}</h4>
        <div className="grid grid-cols-7 gap-0.5 text-xs">
          {['Dl', 'Dm', 'Dc', 'Dj', 'Dv', 'Ds', 'Dg'].map(d => (
            <div key={d} className="text-center text-muted-foreground text-[10px]">{d}</div>
          ))}
          {days.map((date, i) => (
            <div
              key={i}
              className={`h-5 w-full flex items-center justify-center text-[10px] rounded-sm ${
                date ? getDayClass(date) : ''
              }`}
              title={date ? getDayTitle(date) : ''}
            >
              {date?.getDate()}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const totalDaysUsed = summary 
    ? Object.values(summary).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Calendari {year}</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setYear(year - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium w-16 text-center">{year}</span>
          <Button variant="outline" size="icon" onClick={() => setYear(year + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs">Resum d&apos;absencies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
              {Object.entries(ABSENCE_COLORS).map(([key, value]) => (
                <div key={key}>
                  <div className={`w-8 h-8 rounded mx-auto mb-1 flex items-center justify-center text-white font-bold ${value.bg}`}>
                    {summary[key as keyof Summary] || 0}
                  </div>
                  <span className="text-xs text-muted-foreground">{value.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t text-center">
              <span className="text-2xl font-bold">{totalDaysUsed}</span>
              <span className="text-sm text-muted-foreground ml-2">dies d&apos;absencia total</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="font-medium">Llegenda:</span>
        {Object.entries(ABSENCE_COLORS).map(([key, value]) => (
          <div key={key} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${value.bg}`} />
            <span>{value.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-400" />
          <span>Festiu</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-gray-200" />
          <span>Cap de setmana</span>
        </div>
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Carregant calendari...</div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 12 }, (_, i) => renderMonth(i))}
        </div>
      )}
    </div>
  );
}
